/**
 * Number Formatting Utilities
 * Centralized formatting for currency, percentages, and general numbers
 */

/**
 * Format currency values
 * @param {number} value - The numeric value to format
 * @param {string} currency - Currency symbol (default: '£')
 * @param {number} decimals - Number of decimal places (default: smart detection)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = '£', decimals = null) => {
  if (value == null || isNaN(value)) return `${currency}0`

  // Determine decimal places
  let decimalPlaces = decimals
  if (decimalPlaces === null) {
    const absValue = Math.abs(value)

    // For amounts >= £1000, never show decimals
    if (absValue >= 1000) {
      decimalPlaces = 0
    }
    // For amounts < £1, always show 2 decimals
    else if (absValue < 1) {
      decimalPlaces = 2
    }
    // For amounts between £1 and £1000
    else {
      // Check if the decimal part is significant
      const decimalPart = absValue % 1
      // Only show decimals if they're not .00
      decimalPlaces = decimalPart > 0.009 ? 2 : 0
    }
  }

  const formatted = Math.abs(value).toLocaleString('en-GB', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })

  const sign = value < 0 ? '-' : ''
  return `${sign}${currency}${formatted}`
}

/**
 * Format percentage values
 * @param {number} value - The numeric value to format (already in percentage, e.g., 75.5 for 75.5%)
 * @param {number} decimals - Number of decimal places (default: smart detection)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = null) => {
  if (value == null || isNaN(value)) return '-'

  let decimalPlaces = decimals
  if (decimalPlaces === null) {
    const absValue = Math.abs(value)

    // For percentages >= 100%, never show decimals
    if (absValue >= 100) {
      decimalPlaces = 0
    }
    // For percentages >= 10%, show at most 1 decimal
    else if (absValue >= 10) {
      // Check if decimal is significant
      const decimalPart = absValue % 1
      decimalPlaces = decimalPart > 0.09 ? 1 : 0
    }
    // For smaller percentages, show 1 decimal
    else {
      decimalPlaces = 1
    }

    // Special case: if the value is exactly an integer, don't show decimals
    if (Number.isInteger(value)) {
      decimalPlaces = 0
    }
  }

  return `${value.toFixed(decimalPlaces)}%`
}

/**
 * Format general numbers with appropriate precision
 * @param {number} value - The numeric value to format
 * @param {number} decimals - Number of decimal places (default: smart detection)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = null) => {
  if (value == null || isNaN(value)) return '0'

  let decimalPlaces = decimals
  if (decimalPlaces === null) {
    const absValue = Math.abs(value)

    // For large numbers, don't show decimals
    if (absValue >= 1000) {
      decimalPlaces = 0
    } else if (absValue >= 100) {
      decimalPlaces = 1
    } else if (absValue >= 1) {
      decimalPlaces = 2
    } else {
      // For very small numbers, show more precision
      decimalPlaces = 4
    }

    // Check if it's an integer
    if (Number.isInteger(value)) {
      decimalPlaces = 0
    }
  }

  return value.toLocaleString('en-GB', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  })
}

/**
 * Format hours with appropriate unit
 * @param {number} value - The numeric value in hours
 * @returns {string} Formatted hours string
 */
export const formatHours = (value) => {
  if (value == null || isNaN(value)) return '0 hrs'

  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue >= 100) {
    return `${sign}${formatNumber(value, 0)} hrs`
  } else if (absValue >= 10) {
    return `${sign}${formatNumber(value, 1)} hrs`
  } else {
    return `${sign}${formatNumber(value, 2)} hrs`
  }
}

/**
 * Format compact numbers for display in limited space
 * @param {number} value - The numeric value to format
 * @param {string} currency - Optional currency symbol
 * @returns {string} Formatted compact string (e.g., 1.2K, 3.5M)
 */
export const formatCompact = (value, currency = '') => {
  if (value == null || isNaN(value)) return `${currency}0`

  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue >= 1e9) {
    return `${sign}${currency}${(absValue / 1e9).toFixed(1)}B`
  } else if (absValue >= 1e6) {
    return `${sign}${currency}${(absValue / 1e6).toFixed(1)}M`
  } else if (absValue >= 1e3) {
    return `${sign}${currency}${(absValue / 1e3).toFixed(1)}K`
  } else {
    return `${sign}${currency}${absValue.toFixed(0)}`
  }
}

/**
 * Format variance with appropriate sign indicator
 * @param {number} value - The variance value
 * @param {string} format - Format type: 'currency', 'percentage', 'number'
 * @returns {string} Formatted variance string with indicator
 */
export const formatVariance = (value, format = 'number') => {
  if (value == null || isNaN(value)) return '-'

  // For currency, just use formatCurrency which handles negative values properly
  if (format === 'currency') {
    return formatCurrency(value)
  }

  let formatted
  switch (format) {
    case 'percentage':
      formatted = formatPercentage(Math.abs(value))
      break
    default:
      formatted = formatNumber(Math.abs(value))
  }

  if (value > 0) {
    return `+${formatted}`
  } else if (value < 0) {
    return `-${formatted}`
  }
  return formatted
}

/**
 * Format date string
 * @param {string|Date} date - Date string or Date object
 * @param {string} format - Format type: 'short', 'long', 'full'
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) return ''

  const options = {
    short: { day: '2-digit', month: 'short', year: 'numeric' },
    long: { day: 'numeric', month: 'long', year: 'numeric' },
    full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
  }

  return dateObj.toLocaleDateString('en-GB', options[format] || options.short)
}

export default {
  formatCurrency,
  formatPercentage,
  formatNumber,
  formatHours,
  formatCompact,
  formatVariance,
  formatDate,
}